<script setup>
// "Agendas que este agente pode usar" (spec 011): profissionais e usuarios da conta, busca por nome, selo do Google e a
// opcao "Agenda do atendente da conversa". Nada escolhido = todas as agendas, como antes.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgendaScopesAPI from 'dashboard/api/agendaScopes';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  emptyScope,
  hasNoActiveAgenda,
  isEmptyScope,
  matchesSearch,
} from './agendaScopes';

const props = defineProps({
  // Quem lista varios seletores (Cenarios) carrega o catalogo uma vez e repassa.
  catalog: { type: Object, default: null },
});

const model = defineModel({ type: Object, default: emptyScope });

const { t } = useI18n();
const loadedCatalog = ref(null);
const search = ref('');

const data = computed(() => props.catalog || loadedCatalog.value);
const chosen = computed(() => ({ ...emptyScope(), ...model.value }));
const googleEnabled = computed(() => data.value?.google_calendar_enabled);

const practitioners = computed(() =>
  (data.value?.practitioners || []).filter(item =>
    matchesSearch(search.value, item.name, item.specialty, item.user_name)
  )
);
const users = computed(() =>
  (data.value?.users || []).filter(item =>
    matchesSearch(search.value, item.name, item.email)
  )
);
const noResults = computed(
  () => !practitioners.value.length && !users.value.length
);
const noneActive = computed(() => hasNoActiveAgenda(chosen.value, data.value));

const toggleId = (key, id, checked) => {
  const current = chosen.value[key];
  const ids = checked
    ? [...new Set([...current, id])]
    : current.filter(item => item !== id);
  model.value = { ...chosen.value, [key]: ids };
};

const toggleAssignee = value => {
  model.value = { ...chosen.value, conversation_assignee: value };
};

onMounted(async () => {
  if (props.catalog) return;
  try {
    loadedCatalog.value = (await AgendaScopesAPI.catalog()).data.data;
  } catch {
    useAlert(t('AGENDA_SCOPES.LOAD_ERROR'));
  }
});
</script>

<template>
  <div class="flex flex-col gap-4" data-test="agenda-scope-selector">
    <p
      v-if="isEmptyScope(chosen)"
      class="mb-0 text-sm text-n-slate-11"
      data-test="agenda-scope-all"
    >
      {{ t('AGENDA_SCOPES.ALL') }}
    </p>

    <div
      class="flex flex-col gap-1 p-3 border rounded-lg border-n-weak bg-n-solid-1"
      data-test="agenda-scope-assignee"
    >
      <div class="flex items-center justify-between gap-3">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('AGENDA_SCOPES.ASSIGNEE_OPTION') }}
        </span>
        <Switch
          :model-value="chosen.conversation_assignee"
          @update:model-value="toggleAssignee"
        />
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('AGENDA_SCOPES.ASSIGNEE_HELP') }}
      </p>
    </div>

    <p v-if="noneActive" class="mb-0 text-sm text-n-ruby-11" data-test="none-active">
      {{ t('AGENDA_SCOPES.NONE_ACTIVE') }}
    </p>

    <Input
      v-model="search"
      size="sm"
      :placeholder="t('AGENDA_SCOPES.SEARCH')"
      data-test="agenda-scope-search"
    />

    <p v-if="noResults" class="mb-0 text-sm text-n-slate-11">
      {{ t('AGENDA_SCOPES.NO_RESULTS') }}
    </p>

    <section v-if="practitioners.length" class="flex flex-col gap-2">
      <h4 class="mb-0 text-xs font-medium uppercase text-n-slate-11">
        {{ t('AGENDA_SCOPES.PRACTITIONERS') }}
      </h4>
      <label
        v-for="item in practitioners"
        :key="`p-${item.id}`"
        class="flex items-center gap-2 text-sm text-n-slate-12"
        :data-test="`practitioner-${item.id}`"
      >
        <Checkbox
          :model-value="chosen.practitioner_ids.includes(item.id)"
          @update:model-value="
            checked => toggleId('practitioner_ids', item.id, checked)
          "
        />
        <span>{{ item.name }}</span>
        <span v-if="item.specialty" class="text-xs text-n-slate-11">
          {{ item.specialty }}
        </span>
        <span v-if="item.user_name" class="text-xs text-n-slate-11">
          {{ t('AGENDA_SCOPES.LINKED_USER', { name: item.user_name }) }}
        </span>
        <span
          v-if="googleEnabled && item.google_connected"
          class="px-1.5 py-0.5 text-xs rounded bg-n-teal-9/10 text-n-teal-11"
          data-test="google-badge"
        >
          {{ t('AGENDA_SCOPES.GOOGLE_CONNECTED') }}
        </span>
      </label>
    </section>

    <section v-if="users.length" class="flex flex-col gap-2">
      <h4 class="mb-0 text-xs font-medium uppercase text-n-slate-11">
        {{ t('AGENDA_SCOPES.USERS') }}
      </h4>
      <label
        v-for="item in users"
        :key="`u-${item.user_id}`"
        class="flex items-center gap-2 text-sm text-n-slate-12"
        :data-test="`user-${item.user_id}`"
      >
        <Checkbox
          :model-value="chosen.user_ids.includes(item.user_id)"
          @update:model-value="
            checked => toggleId('user_ids', item.user_id, checked)
          "
        />
        <span>{{ item.name }}</span>
        <span class="text-xs text-n-slate-11">{{ item.email }}</span>
        <span
          v-if="googleEnabled && item.google_connected"
          class="px-1.5 py-0.5 text-xs rounded bg-n-teal-9/10 text-n-teal-11"
          data-test="google-badge"
        >
          {{ t('AGENDA_SCOPES.GOOGLE_CONNECTED') }}
        </span>
      </label>
    </section>
  </div>
</template>
